import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Sport } from '@/types';

export type UserSegment = 
  | 'new_user' 
  | 'match_player' 
  | 'training_student' 
  | 'court_booker' 
  | 'club_member' 
  | 'active_all';

export interface UserActivitySummary {
  userId: string;
  preferredSports: Sport[] | null;
  rankedSport: Sport | null;
  skillLevel: string | null;
  location: string | null;
  city: string | null;
  
  // Activity counts
  matchCount: number;
  enrollmentCount: number;
  bookingCount: number;
  clubCount: number;
  followingCount: number;
  favoriteCourtCount: number;
  
  // Recent activity
  recentMatchCount: number;
  recentEnrollmentCount: number;
  recentBookingCount: number;
  
  // Derived data
  primaryActivitySport: Sport | null;
  activeEnrollmentCount: number;
  upcomingSessionCount: number;
  userSegment: UserSegment;
  lastActivityAt: string;
}

export function useUserActivitySummary() {
  const { profile } = useAuth();
  
  return useQuery({
    queryKey: ['user-activity-summary', profile?.id],
    queryFn: async (): Promise<UserActivitySummary | null> => {
      if (!profile?.id) return null;
      
      // Fetch activity counts in parallel
      const [
        matchResult,
        enrollmentResult,
        bookingResult,
        clubResult,
        followingResult,
        favoriteCourtResult,
        recentMatchResult,
        activeEnrollmentResult,
        upcomingSessionResult,
      ] = await Promise.all([
        // Total match count
        supabase
          .from('match_participants')
          .select('id', { count: 'exact', head: true })
          .eq('profile_id', profile.id),
        
        // Total enrollment count
        supabase
          .from('class_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', profile.id),
        
        // Total booking count
        supabase
          .from('court_booking_requests')
          .select('id', { count: 'exact', head: true })
          .eq('player_id', profile.id),
        
        // Club count
        supabase
          .from('club_members')
          .select('id', { count: 'exact', head: true })
          .eq('profile_id', profile.id),
        
        // Following count
        supabase
          .from('user_follows')
          .select('id', { count: 'exact', head: true })
          .eq('follower_id', profile.id),
        
        // Favorite court count
        supabase
          .from('favorite_courts')
          .select('id', { count: 'exact', head: true })
          .eq('profile_id', profile.id),
        
        // Recent match count (last 30 days)
        supabase
          .from('match_participants')
          .select('match_id, matches!inner(date)', { count: 'exact', head: true })
          .eq('profile_id', profile.id)
          .gte('matches.date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]),
        
        // Active enrollment count
        supabase
          .from('class_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', profile.id)
          .eq('status', 'approved')
          .eq('payment_status', 'paid'),
        
        // Upcoming session count
        supabase
          .from('class_session_history')
          .select('id', { count: 'exact', head: true })
          .eq('student_id', profile.id)
          .eq('status', 'scheduled')
          .gte('session_date', new Date().toISOString().split('T')[0]),
      ]);
      
      const matchCount = matchResult.count ?? 0;
      const enrollmentCount = enrollmentResult.count ?? 0;
      const bookingCount = bookingResult.count ?? 0;
      const clubCount = clubResult.count ?? 0;
      const followingCount = followingResult.count ?? 0;
      const favoriteCourtCount = favoriteCourtResult.count ?? 0;
      const recentMatchCount = recentMatchResult.count ?? 0;
      const activeEnrollmentCount = activeEnrollmentResult.count ?? 0;
      const upcomingSessionCount = upcomingSessionResult.count ?? 0;
      
      // Determine user segment
      let userSegment: UserSegment = 'new_user';
      
      if (matchCount === 0 && enrollmentCount === 0 && bookingCount === 0) {
        userSegment = 'new_user';
      } else if (activeEnrollmentCount > 0) {
        userSegment = 'training_student';
      } else if (matchCount > enrollmentCount * 2) {
        userSegment = 'match_player';
      } else if (bookingCount > 3) {
        userSegment = 'court_booker';
      } else if (clubCount >= 2) {
        userSegment = 'club_member';
      } else {
        userSegment = 'active_all';
      }
      
      // Get most played sport
      let primaryActivitySport: Sport | null = null;
      const { data: sportData } = await supabase
        .from('match_participants')
        .select('matches!inner(sport)')
        .eq('profile_id', profile.id)
        .limit(50);
      
      if (sportData && sportData.length > 0) {
        const sportCounts: Record<string, number> = {};
        sportData.forEach((row: any) => {
          const sport = row.matches?.sport;
          if (sport) {
            sportCounts[sport] = (sportCounts[sport] || 0) + 1;
          }
        });
        
        const topSport = Object.entries(sportCounts)
          .sort(([, a], [, b]) => b - a)[0];
        if (topSport) {
          primaryActivitySport = topSport[0] as Sport;
        }
      }
      
      return {
        userId: profile.id,
        preferredSports: profile.sports as Sport[] | null,
        rankedSport: profile.ranked_sport as Sport | null,
        skillLevel: profile.skill_level,
        location: profile.location,
        city: profile.city,
        matchCount,
        enrollmentCount,
        bookingCount,
        clubCount,
        followingCount,
        favoriteCourtCount,
        recentMatchCount,
        recentEnrollmentCount: enrollmentCount, // Simplified
        recentBookingCount: bookingCount, // Simplified
        primaryActivitySport,
        activeEnrollmentCount,
        upcomingSessionCount,
        userSegment,
        lastActivityAt: profile.updated_at ?? new Date().toISOString(),
      };
    },
    enabled: !!profile?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in garbage collection for 10 minutes
  });
}
